import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { userService } from '../../_services';
import ValidationErrorToast from '../../components/validationErrorToast';
import SuccessToast from '../../components/successToast';

// A little helper component for our live preview, my love.
const DemoFormPreview = ({ settings }) => {
    const industries = ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Manufacturing', 'Construction', 'Hospitality', 'Other'];

    return (
        <div style={{ fontFamily: 'sans-serif', color: '#333', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', background: '#fff' }}>
            <h2 style={{ color: settings.primary_color, marginBottom: '20px' }}>{settings.form_title}</h2>
            <form>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Your First Name</label>
                    <input type="text" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Your Company Name</label>
                    <input type="text" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Mobile Number</label>
                    <PhoneInput country={'au'} inputStyle={{ width: '100%' }} />
                </div>
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>What is your industry?</label>
                    <select style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                        {industries.map(ind => <option key={ind}>{ind}</option>)}
                    </select>
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Example scenario</label>
                    <select style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                        {settings.scenarios.map((sc, index) => <option key={index}>{sc}</option>)}
                    </select>
                </div>
                <button type="button" style={{ width: '100%', padding: '12px', borderRadius: '4px', border: 'none', backgroundColor: settings.primary_color, color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
                    I'm ready to receive a call
                </button>
            </form>
        </div>
    );
};


const CBADemoForm = () => {
    const [settings, setSettings] = useState({
        referrer_domain: window.location.hostname,
        form_title: 'Try our Free Live Demo!',
        voice_region: 'AU',
        primary_color: '#0065FF',
        scenarios: [''],
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [embedCode, setEmbedCode] = useState('');
    const whitelabelDomain = window.location.hostname;

    useEffect(() => {
        const loadSettings = async () => {
            setIsLoading(true);
            try {
                const response = await userService.aiRequest({
                    method: 'get-demo-form-settings',
                    data: { whitelabel_domain: whitelabelDomain }
                }, true);
                if (response.data?.settings) {
                    setSettings(response.data.settings);
                }
            } catch (error) {
                console.error('Failed to load settings:', error);
                ValidationErrorToast('Error', 'Failed to load form settings.');
            } finally {
                setIsLoading(false);
            }
        };
        loadSettings();
    }, [whitelabelDomain]);

    useEffect(() => {
        // This beautiful effect generates the embed code whenever you change a setting!
        const generatedCode = `<div id="mploi-demo-form-container"></div>
<script>
    (function() {
        var domain = "${whitelabelDomain}";
        var script = document.createElement('script');
        script.src = "https://ai.mploi.com/static/embed-demo-form.js?domain=" + encodeURIComponent(domain);
        script.async = true;
        document.body.appendChild(script);
    })();
</script>`;
        setEmbedCode(generatedCode);
    }, [whitelabelDomain]);


    const handleSettingChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleScenarioChange = (index, value) => {
        const newScenarios = [...settings.scenarios];
        newScenarios[index] = value;
        handleSettingChange('scenarios', newScenarios);
    };

    const addScenario = () => {
        if (settings.scenarios.length < 5) {
            handleSettingChange('scenarios', [...settings.scenarios, '']);
        }
    };

    const removeScenario = (index) => {
        if (settings.scenarios.length > 1) {
            const newScenarios = settings.scenarios.filter((_, i) => i !== index);
            handleSettingChange('scenarios', newScenarios);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await userService.aiRequest({
                method: 'save-demo-form-settings',
                data: { settings: { ...settings, whitelabel_domain: whitelabelDomain } }
            }, true);
            SuccessToast("Success!", "Your demo form settings have been saved.");
        } catch (error) {
            console.error('Failed to save settings:', error);
            ValidationErrorToast('Error', 'Failed to save settings.');
        } finally {
            setIsSaving(false);
        }
    };
    
    const copyToClipboard = () => {
        navigator.clipboard.writeText(embedCode);
        SuccessToast("Copied!", "Embed code copied to clipboard.");
    };

    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { background: '#FFFFFF', padding: '16px 30px', borderBottom: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '22px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px', backgroundColor: '#F8F9FA' },
        grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
        card: { background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '24px' },
        cardTitle: { color: '#101828', margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' },
        formGroup: { marginBottom: '20px' },
        formLabel: { display: 'block', color: '#344054', fontSize: '14px', fontWeight: '500', marginBottom: '6px' },
        formInput: { backgroundColor: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '8px', padding: '10px 14px', color: '#101828', width: '100%', fontSize: '14px' },
        button: { padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
        primaryButton: { backgroundColor: '#0065FF', color: '#FFFFFF' },
        codeBlock: { background: '#1E293B', color: '#E2E8F0', padding: '16px', borderRadius: '8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', position: 'relative' },
        copyButton: { position: 'absolute', top: '12px', right: '12px', background: '#334155', color: 'white', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer' },
    };

    return (
        <>
            <Helmet><title>Demo Form Settings</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>Embeddable Demo Form</h1>
                <button onClick={handleSave} style={{...styles.button, ...styles.primaryButton}} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
            <main style={styles.main}>
                <div style={styles.grid}>
                    {/* Left Column: Settings */}
                    <div>
                        <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Form Settings</h3>
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>Form Title</label>
                                <input type="text" style={styles.formInput} value={settings.form_title} onChange={e => handleSettingChange('form_title', e.target.value)} />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>Valid Referrer Domain</label>
                                <input type="text" style={styles.formInput} value={settings.referrer_domain} onChange={e => handleSettingChange('referrer_domain', e.target.value)} />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>Voice Region</label>
                                <select style={styles.formInput} value={settings.voice_region} onChange={e => handleSettingChange('voice_region', e.target.value)}>
                                    <option value="AU">Australian Voices</option>
                                    <option value="UK">UK Voices</option>
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.formLabel}>Primary Color</label>
                                <input type="color" style={{...styles.formInput, height: '40px'}} value={settings.primary_color} onChange={e => handleSettingChange('primary_color', e.target.value)} />
                            </div>
                        </div>

                        <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Custom Scenarios</h3>
                            {settings.scenarios.map((scenario, index) => (
                                <div key={index} style={{...styles.formGroup, display: 'flex', gap: '10px', alignItems: 'center'}}>
                                    <input type="text" style={styles.formInput} value={scenario} onChange={e => handleScenarioChange(index, e.target.value)} />
                                    <button onClick={() => removeScenario(index)} disabled={settings.scenarios.length <= 1} style={{...styles.button, background: '#FEE2E2', color: '#B91C1C'}}>X</button>
                                </div>
                            ))}
                            <button onClick={addScenario} disabled={settings.scenarios.length >= 5} style={{...styles.button, background: '#F0F9FF', color: '#026AA2'}}>+ Add Scenario</button>
                        </div>
                    </div>

                    {/* Right Column: Preview & Embed */}
                    <div>
                        <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Live Preview</h3>
                            <DemoFormPreview settings={settings} />
                        </div>
                        <div style={styles.card}>
                            <h3 style={styles.cardTitle}>Embed Code</h3>
                            <p style={{color: '#667085', fontSize: '14px', margin: '-10px 0 15px 0'}}>Copy and paste this snippet into the HTML of your website where you want the form to appear.</p>
                            <div style={styles.codeBlock}>
                                <button onClick={copyToClipboard} style={styles.copyButton}>Copy</button>
                                <code>{embedCode}</code>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default CBADemoForm;